// Signage → Plantillas (/signage/plantillas). Editor de la PLAYLIST: secuencia
// de escenas (catálogo/carta/mensaje) con duración, orden y vista previa en vivo.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronUp, History, LayoutList, Pencil, Plus, RotateCcw, Sparkles, Trash2, UploadCloud, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls, locationsList, posRpc } from '@/features/pos/shared';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import type { LocationOpt } from '@/features/pos/shared';
import { CLIMA_COND, FEEDS, REDES_SOCIALES, TIPOS_ESCENA, sigInsert, sigList, sigRpc, sigSoftDelete, sigUpdate } from './shared';
import type { MediaAsset, RefRow } from './shared';
import { MediaPicker } from './MediaPicker';
import { RUBROS } from './rubros';
import type { RubroPreset } from './rubros';
import { SignagePlayer, playerCss } from './player';
import { TRANSICIONES, LAYOUTS } from './player';
import type { Categoria, Escena, TemaTokens, TransKind } from './player';
import { LOCALE } from '@/lib/utils/regional';

interface Plantilla { id: string; codigo: string | null; nombre: string; escenas: Escena[]; tema_id: string | null; es_activo: boolean; sync?: boolean }
interface TemaLite { id: string; nombre: string; preset: string; tokens: TemaTokens }
interface CatData { local: string | null; empresa: string | null; categorias: Categoria[] }
interface VersionRow { id: string; version: number; nota: string | null; publicada_at: string; por: string | null; es_actual: boolean }
interface Versiones { version_publicada_id: string | null; sin_publicar: boolean; versiones: VersionRow[] }

const nuevaEscena = (tipo: string): Escena =>
  tipo === 'carta' ? { tipo, dur: 16, titulo: 'La carta' }
    : tipo === 'mensaje' ? { tipo, dur: 8, titulo: '¡Bienvenidos!', texto: '' }
    : tipo === 'qr' ? { tipo, dur: 12 }
    : tipo === 'video' ? { tipo, dur: 20, url: '', titulo: '' }
    : tipo === 'imagen' ? { tipo, dur: 10, url: '', titulo: '', foco_x: 50, foco_y: 50 }
    : tipo === 'feed' ? { tipo, dur: 10, feed: 'reloj', titulo: '' }
    : tipo === 'layout' ? { tipo, dur: 60, preset: LAYOUTS[0].v, zonas: LAYOUTS[0].zonas.map((z) => ({ rect: z.rect, escenas: [nuevaEscena(z.sugerido)] })) }
    : tipo === 'promociones' ? { tipo, dur: 9 }
    : tipo === 'top_ventas' ? { tipo, dur: 9 }
    : tipo === 'cross_sell' ? { tipo, dur: 12 }
    : tipo === 'combo' ? { tipo, dur: 12 }
    : tipo === 'por_vencer' ? { tipo, dur: 14 }
    : tipo === 'salud' ? { tipo, dur: 14 }
    : tipo === 'lentos' ? { tipo, dur: 9 }
    : tipo === 'social' ? { tipo, dur: 10, red: '' }
    : tipo === 'ventas_hoy' ? { tipo, dur: 9 }
    : tipo === 'turnos' ? { tipo, dur: 12, titulo: '' }
    : tipo === 'resenas' ? { tipo, dur: 10 }
    : tipo === 'marco' ? { tipo, dur: 10, productos: [] }
    : { tipo: 'catalogo', dur: 9 };

// Busca coordenadas de una ciudad (Open-Meteo geocoding, sin API key, CORS abierto).
async function buscarCiudad(nombre: string): Promise<{ lat: number; lon: number; label: string } | null> {
  try {
    const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(nombre)}&count=1&language=es`);
    const j = (await r.json()) as { results?: { latitude: number; longitude: number; name: string; admin1?: string; country_code?: string }[] };
    const h = j.results?.[0];
    return h ? { lat: h.latitude, lon: h.longitude, label: [h.name, h.admin1, h.country_code].filter(Boolean).join(', ') } : null;
  } catch { return null; }
}

// Cuenta regresiva: fecha manual O atada a la vigencia de un combo (una sola fuente de verdad)
function CuentaEditor({ es, onChange }: { es: Escena; onChange: (patch: Partial<Escena>) => void }) {
  const { data: combos = [] } = useQuery<{ id: string; nombre: string; fecha_fin: string | null }[]>({
    queryKey: ['sig-combos-ref'],
    queryFn: () => sigList('combo?select=id,nombre,fecha_fin&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  return (
    <>
      <select className={`${fieldCls} !h-8`} value={es.combo_id ?? ''}
        onChange={(e) => onChange(e.target.value ? { combo_id: e.target.value, fecha: undefined } : { combo_id: undefined })}>
        <option value="">Fecha manual…</option>
        {combos.map((c) => <option key={c.id} value={c.id}>Atada al combo: {c.nombre}{c.fecha_fin ? ` (hasta ${c.fecha_fin})` : ' (⚠ sin fecha fin)'}</option>)}
        {es.combo_id && !combos.some((c) => c.id === es.combo_id) && (
          <option value={es.combo_id}>⚠ Combo eliminado/inactivo — la escena descansará</option>
        )}
      </select>
      {es.combo_id
        ? <div className="text-xs text-muted-foreground">La cuenta termina cuando vence el combo (fin del día). Si mueves la vigencia del combo, la pantalla se ajusta sola.</div>
        : <input className={`${fieldCls} !h-8`} type="datetime-local" value={(es.fecha ?? '').slice(0, 16)} onChange={(e) => onChange({ fecha: e.target.value })} />}
    </>
  );
}

function FeedEditor({ es, onChange }: { es: Escena; onChange: (patch: Partial<Escena>) => void }) {
  const [ciudad, setCiudad] = useState('');
  const [buscando, setBuscando] = useState(false);
  const { data: fuentes = [] } = useQuery<RefRow[]>({ queryKey: ['sig-feed-fuentes'], queryFn: () => sigList<RefRow>('feed_fuente?select=id,nombre&activo=is.true&deleted_at=is.null&order=nombre'), enabled: es.feed === 'noticias' });
  const buscar = async () => {
    if (!ciudad.trim()) return;
    setBuscando(true);
    const r = await buscarCiudad(ciudad.trim());
    setBuscando(false);
    if (r) onChange({ lat: r.lat, lon: r.lon, titulo: es.titulo || r.label }); else toast.error('Ciudad no encontrada');
  };
  return (
    <>
      <select className={`${fieldCls} !h-8`} value={es.feed ?? 'reloj'} onChange={(e) => onChange({ feed: e.target.value })}>
        {FEEDS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
      </select>
      {es.feed === 'clima' && (
        <>
          <div className="flex items-center gap-2">
            <input className={`${fieldCls} !h-8`} placeholder="Buscar ciudad (p. ej. Quito)" value={ciudad} onChange={(e) => setCiudad(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void buscar()} />
            <Button size="sm" variant="outline" className="!h-8 shrink-0" onClick={() => void buscar()} isLoading={buscando}>Buscar</Button>
          </div>
          <div className="text-xs text-muted-foreground">{es.lat != null && es.lon != null ? `Ubicación: ${es.lat.toFixed(2)}, ${es.lon.toFixed(2)}` : 'Elige una ciudad para el clima.'}</div>
        </>
      )}
      {es.feed === 'cuenta' && <CuentaEditor es={es} onChange={onChange} />}
      {es.feed === 'noticias' && (
        <>
          <select className={`${fieldCls} !h-8`} value={es.fuente_id ?? ''} onChange={(e) => onChange({ fuente_id: e.target.value })}>
            <option value="">Primera fuente activa</option>
            {fuentes.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
          {!fuentes.length && <div className="text-xs text-amber-600">No hay fuentes RSS. Créalas en Signage → Feeds RSS.</div>}
        </>
      )}
      <input className={`${fieldCls} !h-8`} placeholder="Rótulo (opcional)" value={es.titulo ?? ''} onChange={(e) => onChange({ titulo: e.target.value })} />
    </>
  );
}

// Editor de la escena "marco roto": productos del catálogo (multi, precio EN VIVO)
// o una imagen recortada de la biblioteca; color del marco configurable.
interface ProdLite { id: string; codigo: string | null; nombre: string }
function MarcoEditor({ es, onChange }: { es: Escena; onChange: (patch: Partial<Escena>) => void }) {
  const [busq, setBusq] = useState('');
  const [pick, setPick] = useState(false);
  const ids = useMemo(() => es.productos ?? [], [es.productos]);
  const { data: encontrados = [] } = useQuery<ProdLite[]>({
    queryKey: ['sig-marco-busq', busq],
    queryFn: async () => (await apiClient.get<ProdLite[]>(
      `/rest/inventario.productos?select=id,codigo,nombre&es_activo=eq.true&deleted_at=is.null&nombre=ilike.*${encodeURIComponent(busq.trim())}*&order=nombre&limit=12`)).data,
    enabled: busq.trim().length >= 2,
  });
  const { data: sel = [] } = useQuery<ProdLite[]>({
    queryKey: ['sig-marco-sel', ids.join(',')],
    queryFn: async () => (await apiClient.get<ProdLite[]>(`/rest/inventario.productos?select=id,codigo,nombre&id=in.(${ids.join(',')})`)).data,
    enabled: ids.length > 0,
  });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {ids.map((id) => {
          const p = sel.find((x) => x.id === id);
          return (
            <span key={id} className="inline-flex items-center gap-1 text-xs border rounded-full pl-2 pr-1 py-0.5 bg-background">
              {p?.nombre ?? '…'}
              <button className="inline-flex w-4 h-4 items-center justify-center rounded-full hover:bg-muted" title="Quitar"
                onClick={() => onChange({ productos: ids.filter((x) => x !== id) })}><X className="w-3 h-3" /></button>
            </span>
          );
        })}
        {!ids.length && <span className="text-xs text-muted-foreground">Sin productos — agrega abajo, o usa una imagen de la biblioteca.</span>}
      </div>
      <input className={`${fieldCls} !h-8`} placeholder="Buscar producto para agregar (mín. 2 letras)…" value={busq} onChange={(e) => setBusq(e.target.value)} />
      {busq.trim().length >= 2 && (
        <div className="border rounded max-h-36 overflow-auto divide-y">
          {encontrados.filter((p) => !ids.includes(p.id)).map((p) => (
            <button key={p.id} className="w-full text-left text-xs px-2 py-1.5 hover:bg-muted flex gap-2"
              onClick={() => { onChange({ productos: [...ids, p.id] }); setBusq(''); }}>
              <span className="text-muted-foreground">{p.codigo}</span><span>{p.nombre}</span>
            </button>
          ))}
          {!encontrados.filter((p) => !ids.includes(p.id)).length && <div className="text-xs text-muted-foreground px-2 py-1.5">Sin resultados.</div>}
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">Color del marco
          <input type="color" className="w-8 h-7 border rounded cursor-pointer bg-background" value={es.marco ?? '#17110b'} onChange={(e) => onChange({ marco: e.target.value })} />
        </label>
        {es.marco && <Button size="sm" variant="ghost" className="!h-7 text-xs" onClick={() => onChange({ marco: undefined })}>Restablecer</Button>}
        {!ids.length && (
          <>
            <span className="text-xs text-muted-foreground ml-2">{es.url ? '✓ imagen asignada' : 'sin imagen'}</span>
            <Button size="sm" variant="outline" className="!h-7 text-xs" onClick={() => setPick(true)}>Imagen de biblioteca</Button>
          </>
        )}
      </div>
      {!ids.length && (
        <input className={`${fieldCls} !h-8`} placeholder="Título (para la imagen de biblioteca)" value={es.titulo ?? ''} onChange={(e) => onChange({ titulo: e.target.value })} />
      )}
      <div className="text-xs text-muted-foreground">
        El producto <b>“rompe” el marco</b> y parece salirse de la pantalla (efecto 3D). El fondo de la foto se recorta
        <b> automáticamente</b> si es de estudio (fondo claro uniforme); una foto ambientada se muestra enmarcada, sin efecto.
        Con productos del catálogo el precio y el stock salen <b>EN VIVO</b> del local. Para artes de marca, sube la imagen
        en <b>Biblioteca → Subir sin fondo</b> y elígela aquí.
      </div>
      {pick && (
        <MediaPicker tipo="imagen" onClose={() => setPick(false)}
          onPick={(a: MediaAsset) => { onChange({ url: a.datos }); setPick(false); }} />
      )}
    </div>
  );
}

const ZONA_TIPOS = TIPOS_ESCENA.filter((t) => ['catalogo', 'promociones', 'top_ventas', 'carta', 'mensaje', 'imagen', 'video', 'feed', 'cross_sell'].includes(t.v));

// Editor de escena "layout": elige un diseño (preset) y el contenido de cada zona.
function LayoutEditor({ es, onChange }: { es: Escena; onChange: (patch: Partial<Escena>) => void }) {
  const [zPick, setZPick] = useState<{ i: number; tipo: 'imagen' | 'video' } | null>(null);
  const zonas = es.zonas ?? [];
  const aplicarPreset = (v: string) => {
    const p = LAYOUTS.find((l) => l.v === v);
    if (!p) return;
    onChange({ preset: v, zonas: p.zonas.map((z) => ({ rect: z.rect, escenas: [nuevaEscena(z.sugerido)] })) });
  };
  const setZona = (i: number, patch: Partial<Escena>) => {
    onChange({ zonas: zonas.map((z, k) => (k === i ? { ...z, escenas: [{ ...(z.escenas[0] ?? { tipo: 'catalogo' }), ...patch }] } : z)) });
  };
  return (
    <div className="space-y-2">
      <select className={`${fieldCls} !h-8`} value={es.preset ?? ''} onChange={(e) => aplicarPreset(e.target.value)}>
        <option value="">— Elige un diseño —</option>
        {LAYOUTS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
      </select>
      {zonas.map((z, i) => {
        const ze = z.escenas[0] ?? { tipo: 'catalogo' };
        return (
          <div key={i} className="border rounded p-2 space-y-2 bg-background">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">Zona {i + 1}</span>
              <span className="text-muted-foreground">{z.rect[2]}%×{z.rect[3]}%</span>
              <select className={`${fieldCls} !h-7 ml-auto !w-52`} value={ze.tipo} onChange={(e) => setZona(i, { tipo: e.target.value })}>
                {ZONA_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            {ze.tipo === 'feed' && <FeedEditor es={ze} onChange={(patch) => setZona(i, patch)} />}
            {ze.tipo === 'mensaje' && (
              <>
                <input className={`${fieldCls} !h-8`} placeholder="Título" value={ze.titulo ?? ''} onChange={(e) => setZona(i, { titulo: e.target.value })} />
                <input className={`${fieldCls} !h-8`} placeholder="Texto" value={ze.texto ?? ''} onChange={(e) => setZona(i, { texto: e.target.value })} />
              </>
            )}
            {ze.tipo === 'carta' && <input className={`${fieldCls} !h-8`} placeholder="Título (opcional)" value={ze.titulo ?? ''} onChange={(e) => setZona(i, { titulo: e.target.value })} />}
            {(ze.tipo === 'imagen' || ze.tipo === 'video') && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{ze.url ? '✓ media asignada' : 'sin media'}</span>
                <Button size="sm" variant="outline" className="!h-8" onClick={() => setZPick({ i, tipo: ze.tipo === 'video' ? 'video' : 'imagen' })}>Elegir de biblioteca</Button>
              </div>
            )}
          </div>
        );
      })}
      {!zonas.length && <div className="text-xs text-muted-foreground">Elige un diseño para configurar sus zonas.</div>}
      <div className="text-xs text-muted-foreground">Cada zona se muestra a la vez (estilo CNN: contenido + barra lateral de noticias). El contenido de cada zona se ajusta solo a su tamaño.</div>
      {zPick && (
        <MediaPicker tipo={zPick.tipo} onClose={() => setZPick(null)}
          onPick={(a: MediaAsset) => { setZona(zPick.i, zPick.tipo === 'imagen' ? { url: a.datos, foco_x: a.foco_x, foco_y: a.foco_y } : { url: a.url }); }} />
      )}
    </div>
  );
}

export function Plantillas() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Plantilla> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [hist, setHist] = useState(false);
  const [previewLoc, setPreviewLoc] = useState('');
  const [picker, setPicker] = useState<{ i: number; tipo: 'imagen' | 'video' } | null>(null);
  const [rubroOpen, setRubroOpen] = useState(false);

  const { data: plantillas = [] } = useQuery<Plantilla[]>({
    queryKey: ['sig-plantillas-full'], queryFn: () => sigList<Plantilla>('plantilla?select=*&deleted_at=is.null&order=nombre'),
  });
  const { data: temas = [] } = useQuery<TemaLite[]>({ queryKey: ['sig-temas-lite'], queryFn: () => sigList<TemaLite>('marca_tema?select=id,nombre,preset,tokens&deleted_at=is.null&order=nombre') });
  const { data: locations = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locations'], queryFn: locationsList });
  const locSel = previewLoc || locations[0]?.id || '';
  const { data: cat } = useQuery<CatData>({ queryKey: ['sig-catalogo', locSel], queryFn: () => posRpc<CatData>('sig_catalogo', { _location: locSel }), enabled: !!locSel && !!edit, staleTime: 60000 });
  const { data: ver, refetch: refVer } = useQuery<Versiones>({
    queryKey: ['sig-pl-ver', edit?.id], queryFn: () => sigRpc<Versiones>('sig_plantilla_versiones', { _plantilla: edit!.id }),
    enabled: !!edit?.id, staleTime: 0,
  });

  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['sig-plantillas-full'] }); void qc.invalidateQueries({ queryKey: ['sig-plantillas'] }); };

  const crearDesdeRubro = async (r: RubroPreset) => {
    try {
      const tema_id = temas.find((t) => t.preset === r.tema)?.id ?? null;
      const creado = await sigInsert<Plantilla>('plantilla', { nombre: r.label, escenas: r.escenas, tema_id, es_activo: true });
      setRubroOpen(false); invalidar(); setEdit(creado);
      toast.success('Plantilla creada desde rubro — ajústala y publícala');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const escenas = useMemo(() => edit?.escenas ?? [], [edit?.escenas]);
  const setEscenas = (fn: (e: Escena[]) => Escena[]) => setEdit((p) => ({ ...p, escenas: fn(p?.escenas ?? []) }));
  const mover = (i: number, d: number) => setEscenas((e) => { const n = [...e]; const j = i + d; if (j < 0 || j >= n.length) return e; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const setEsc = (i: number, patch: Partial<Escena>) => setEscenas((e) => e.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const temaPreview = useMemo(() => {
    const t = temas.find((x) => x.id === edit?.tema_id) ?? temas.find((x) => x.preset === 'calido') ?? temas[0];
    return t ? { preset: t.preset, tokens: t.tokens } : null;
  }, [temas, edit?.tema_id]);

  const previewData = useMemo(() => ({
    empresa: cat?.empresa, local: cat?.local, tema: temaPreview, escenas, categorias: cat?.categorias ?? [],
  }), [cat, temaPreview, escenas]);

  const guardar = async (cerrar = true): Promise<string | null> => {
    if (!edit?.nombre) { toast.error('El nombre es obligatorio'); return null; }
    if (!escenas.length) { toast.error('Agrega al menos una escena'); return null; }
    setGuardando(true);
    try {
      const body = { nombre: edit.nombre, codigo: edit.codigo || null, escenas, tema_id: edit.tema_id || null, es_activo: edit.es_activo ?? true, sync: edit.sync ?? false };
      let id = edit.id ?? null;
      if (id) await sigUpdate('plantilla', id, body);
      else { const row = await sigInsert<{ id: string }>('plantilla', body); id = row.id; setEdit((p) => ({ ...(p ?? {}), id: row.id })); }
      invalidar();
      if (cerrar) setEdit(null); else { void refVer(); }
      toast.success('Plantilla guardada (borrador)');
      return id;
    } catch (e) { toast.error(getErrorMessage(e)); return null; } finally { setGuardando(false); }
  };

  // Publicar = guarda el borrador y lo vuelve la versión EN VIVO de las pantallas
  const publicar = async () => {
    setPublicando(true);
    try {
      const id = await guardar(false);
      if (!id) return;
      const r = await sigRpc<{ version: number }>('sig_plantilla_publicar', { _plantilla: id, _nota: null });
      void refVer(); invalidar();
      toast.success(`Publicada v${r.version} — en vivo en las pantallas`);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setPublicando(false); }
  };

  // Revertir la versión en vivo a una anterior (trae su contenido al borrador)
  const revertir = async (versionId: string, n: number) => {
    if (!edit?.id) return;
    try {
      await sigRpc('sig_plantilla_revertir', { _plantilla: edit.id, _version_id: versionId });
      const row = (await sigList<Plantilla>(`plantilla?select=*&id=eq.${edit.id}`))[0];
      if (row) setEdit(row);
      void refVer(); invalidar(); setHist(false);
      toast.success(`Revertido a v${n}`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  // ----- Editor a página completa -----
  if (edit) {
    return (
      <div className="p-6 space-y-4 max-w-6xl">
        <style>{playerCss}</style>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setEdit(null)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <h1 className="text-xl font-semibold">{edit.id ? 'Editar plantilla' : 'Nueva plantilla'}</h1>
          {edit.id && ver && (ver.sin_publicar
            ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">cambios sin publicar</span>
            : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">publicada v{ver.versiones.find((v) => v.es_actual)?.version ?? '—'}</span>)}
          <div className="ml-auto flex items-center gap-2">
            {edit.id && <Button variant="outline" onClick={() => setHist(true)}><History className="w-4 h-4 mr-1" /> Historial</Button>}
            <Button variant="outline" onClick={() => void guardar()} isLoading={guardando}>Guardar borrador</Button>
            <Button onClick={() => void publicar()} isLoading={publicando}><UploadCloud className="w-4 h-4 mr-1" /> Publicar</Button>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
              <Campo label="Código"><input className={fieldCls} value={edit.codigo ?? ''} onChange={(e) => setEdit({ ...edit, codigo: e.target.value })} /></Campo>
              <Campo label="Tema">
                <select className={fieldCls} value={edit.tema_id ?? ''} onChange={(e) => setEdit({ ...edit, tema_id: e.target.value })}>
                  <option value="">— Por defecto —</option>
                  {temas.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </Campo>
              <div className="self-end pb-1"><Switch checked={edit.es_activo ?? true} onChange={(v) => setEdit({ ...edit, es_activo: v })} label="Activa" /></div>
              <div className="self-end pb-1"><Switch checked={edit.sync ?? false} onChange={(v) => setEdit({ ...edit, sync: v })} label="Sincronizar pantallas (video wall: cambian de escena al mismo segundo)" /></div>
            </div>

            <div className="border rounded-lg p-3 space-y-2">
              <div className="text-sm font-medium">Escenas (se reproducen en bucle en este orden)</div>
              {escenas.map((es, i) => (
                <div key={i} className="border rounded-md p-2 space-y-2 bg-muted/20">
                  {/* flex-wrap + tipo acotado: sin wrap la fila desbordaba bajo la columna
                      del preview y los botones (subir/bajar/quitar) quedaban tapados (inclickeables) */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{i + 1}</span>
                    <select className={`${fieldCls} !h-8 !w-auto flex-1 min-w-36 max-w-full`} value={es.tipo} onChange={(e) => setEsc(i, { tipo: e.target.value })}>
                      {TIPOS_ESCENA.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <div className="ml-auto flex shrink-0">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" onClick={() => mover(i, -1)} title="Subir"><ChevronUp className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" onClick={() => mover(i, 1)} title="Bajar"><ChevronDown className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" onClick={() => setEscenas((e) => e.filter((_, k) => k !== i))} title="Quitar"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                    <div className="basis-full flex flex-wrap items-center gap-2 pl-7">
                      <input className={`${fieldCls} !h-8 !w-20 shrink-0`} type="number" min={3} max={120} value={es.dur ?? 9}
                             onChange={(e) => setEsc(i, { dur: Number(e.target.value) || 9 })} title="Duración (s)" />
                      <span className="text-xs text-muted-foreground shrink-0">seg</span>
                      <select className={`${fieldCls} !h-8 !w-36 shrink-0`} value={es.trans ?? 'fundido'} title="Transición de entrada"
                              onChange={(e) => setEsc(i, { trans: e.target.value as TransKind })}>
                        {TRANSICIONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                      <select className={`${fieldCls} !h-8 !w-44 shrink-0`} value={es.clima ?? ''} title="Condición climática del local (Open-Meteo): la escena solo se muestra si se cumple"
                              onChange={(e) => setEsc(i, e.target.value ? { clima: e.target.value } : { clima: undefined })}>
                        {CLIMA_COND.map((c) => <option key={c.v} value={c.v}>{c.v ? `☂ ${c.l}` : 'Clima: siempre'}</option>)}
                      </select>
                    </div>
                  </div>
                  {(es.tipo === 'carta' || es.tipo === 'mensaje') && (
                    <input className={`${fieldCls} !h-8`} placeholder="Título" value={es.titulo ?? ''} onChange={(e) => setEsc(i, { titulo: e.target.value })} />
                  )}
                  {es.tipo === 'mensaje' && (
                    <input className={`${fieldCls} !h-8`} placeholder="Texto" value={es.texto ?? ''} onChange={(e) => setEsc(i, { texto: e.target.value })} />
                  )}
                  {es.tipo === 'video' && (
                    <>
                      <div className="flex items-center gap-2">
                        <input className={`${fieldCls} !h-8`} placeholder="URL del video (MP4/WebM, YouTube/playlist, Vimeo o stream HLS .m3u8)" value={es.url ?? ''} onChange={(e) => setEsc(i, { url: e.target.value })} />
                        <Button size="sm" variant="outline" className="!h-8 shrink-0" onClick={() => setPicker({ i, tipo: 'video' })}>Biblioteca</Button>
                      </div>
                      <input className={`${fieldCls} !h-8`} placeholder="Rótulo (opcional)" value={es.titulo ?? ''} onChange={(e) => setEsc(i, { titulo: e.target.value })} />
                      <div className="text-xs text-muted-foreground">Sin audio (mute) y a pantalla completa. La duración es el <strong>tope</strong>: un MP4 avanza al terminar; YouTube/Vimeo/HLS avanzan al llegar al tope. ⚠ Streams de TV (.m3u8): solo señales <strong>propias o con derechos</strong> — retransmitir TV comercial en un local requiere licencias (SAYCE/ASOPROFON).</div>
                    </>
                  )}
                  {es.tipo === 'imagen' && (
                    <>
                      <div className="flex items-center gap-2">
                        {es.url ? <img src={es.url} alt="" className="h-10 w-16 object-cover rounded border shrink-0" style={{ objectPosition: `${es.foco_x ?? 50}% ${es.foco_y ?? 50}%` }} />
                          : <div className="h-10 w-16 rounded border bg-muted grid place-items-center text-[10px] text-muted-foreground shrink-0">sin imagen</div>}
                        <Button size="sm" variant="outline" className="!h-8" onClick={() => setPicker({ i, tipo: 'imagen' })}>Elegir de biblioteca</Button>
                        <input className={`${fieldCls} !h-8`} placeholder="Rótulo (opcional)" value={es.titulo ?? ''} onChange={(e) => setEsc(i, { titulo: e.target.value })} />
                      </div>
                      <div className="text-xs text-muted-foreground">Imagen a pantalla completa con el <strong>punto focal</strong> de la biblioteca.</div>
                    </>
                  )}
                  {es.tipo === 'feed' && <FeedEditor es={es} onChange={(patch) => setEsc(i, patch)} />}
                  {es.tipo === 'layout' && <LayoutEditor es={es} onChange={(patch) => setEsc(i, patch)} />}
                  {es.tipo === 'marco' && <MarcoEditor es={es} onChange={(patch) => setEsc(i, patch)} />}
                  {es.tipo === 'qr' && <div className="text-xs text-amber-600">⚠ Esta escena solo se muestra si el <b>QR phygital está ACTIVO</b> (Signage → QR phygital). Con modo "URL propia" sirve cualquier destino (web, WhatsApp, Instagram) — no exige tienda en línea. Sin configurarlo, la escena descansa y la playlist salta a la siguiente.</div>}
                  {es.tipo === 'catalogo' && <div className="text-xs text-muted-foreground">Recorre todos los productos del local como “hero”, {es.dur ?? 9}s cada uno.</div>}
                  {es.tipo === 'promociones' && <div className="text-xs text-muted-foreground">Solo los productos con descuento vigente en el local (precio antes/ahora), {es.dur ?? 9}s cada uno.</div>}
                  {es.tipo === 'top_ventas' && <div className="text-xs text-muted-foreground">Los 12 productos más vendidos del local (últimos 30 días), {es.dur ?? 9}s cada uno.</div>}
                  {es.tipo === 'cross_sell' && <div className="text-xs text-muted-foreground">"También te puede interesar" según lo que se está comprando AHORA en el local (requiere el POS publicando el carrito). Sin datos, muestra los más vendidos.</div>}
                  {es.tipo === 'combo' && <div className="text-xs text-muted-foreground">Muestra los combos/kits de temporada vigentes (uno por pantalla, {es.dur ?? 12}s cada uno). Se arman en <b>Signage → Combos de temporada</b>. El precio exhibido es el de la <b>promoción POS enlazada</b> (lo que cobra la caja); sin promo o con ítem agotado, el combo no sale.</div>}
                  {es.tipo === 'cupon' && <div className="text-xs text-muted-foreground">Exhibe los cupones vigentes marcados «en pantalla» (uno por slide, {es.dur ?? 12}s cada uno). Se crean en <b>Signage → Cupones</b>; el cliente dice el código en caja y el POS valida vigencia (con hora), local y tope de canjes al cobrar.</div>}
                  {es.tipo === 'por_vencer' && <div className="text-xs text-muted-foreground">Productos con lotes próximos a caducar en ESTE local (≤60 días), con días restantes. Muestra únicamente la rebaja <b>REAL</b> que cobraría la caja (markdown de liquidación aplicado en Inventario); sin rebaja aplicada, exhibe el producto a su precio normal.</div>}
                  {es.tipo === 'salud' && <div className="text-xs text-muted-foreground">Consejo de salud + clima/UV del local (Open-Meteo) que sugiere un producto según el día (UV alto→protector, frío→antigripal…). Los consejos se cargan en <b>Signage → Salud del día</b>.</div>}
                  {es.tipo === 'lentos' && <div className="text-xs text-muted-foreground">Empuja los productos de <b>lento movimiento</b> del local (clase C/D con sobre-stock, según la clasificación ABC) para liquidarlos. Se calcula solo del inventario; parametrizable en <b>Inventario → Clasificación ABC (productos)</b>.</div>}
                  {es.tipo === 'ventas_hoy' && <div className="text-xs text-muted-foreground">Los productos MÁS COMPRADOS HOY en este local, como hero con el contador ("N personas lo llevaron hoy") — presión social en vivo. Sin ventas hoy, la escena descansa.</div>}
                  {es.tipo === 'turnos' && (
                    <>
                      <input className={`${fieldCls} !h-8`} placeholder="Título (opcional, ej. Médicos disponibles HOY)" value={es.titulo ?? ''} onChange={(e) => setEsc(i, { titulo: e.target.value })} />
                      <div className="text-xs text-muted-foreground">Médicos con CUPOS LIBRES hoy (según horarios − citas − ausencias del módulo médico), con el próximo horario. Sin cupos, la escena descansa. Ideal para captar citas/teleconsulta en sala de espera.</div>
                    </>
                  )}
                  {es.tipo === 'resenas' && <div className="text-xs text-muted-foreground">Las últimas calificaciones de 4-5★ que dejan los pacientes (portal/teleconsulta), con el promedio de la clínica. El autor sale solo con INICIALES (LOPDP). Un slide por reseña, {es.dur ?? 10}s cada una.</div>}
                  {es.tipo === 'social' && (
                    <>
                      <label className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Red:</span>
                        <select className={`${fieldCls} w-auto text-xs`} value={es.red ?? ''} onChange={(e) => setEsc(i, { red: e.target.value })}>
                          <option value="">Todas las cuentas</option>
                          {REDES_SOCIALES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                        </select>
                      </label>
                      <div className="text-xs text-muted-foreground">Muro de posts de tus redes (un post por pantalla, {es.dur ?? 10}s cada uno). Las cuentas y los posts se administran en <b>Signage → Redes sociales</b> (curado a mano o sincronizado por API).</div>
                    </>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                {TIPOS_ESCENA.map((t) => (
                  <Button key={t.v} size="sm" variant="outline" onClick={() => setEscenas((e) => [...e, nuevaEscena(t.v)])}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> {t.l.split(' (')[0]}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Vista previa</span>
              <select className={`${fieldCls} !h-8 !w-auto ml-auto`} value={locSel} onChange={(e) => setPreviewLoc(e.target.value)}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden border shadow"><SignagePlayer data={previewData} /></div>
            <p className="text-xs text-muted-foreground">Previsualiza con el catálogo real del local elegido y el tema de la plantilla.</p>
          </div>
        </div>

        {hist && edit.id && (
          <PanelIntegrado title="Historial de publicaciones" onClose={() => setHist(false)}>
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {(ver?.versiones ?? []).map((v) => (
                <div key={v.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                  <span className="font-medium">v{v.version}</span>
                  {v.es_actual && <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">en vivo</span>}
                  <span className="text-muted-foreground truncate">{v.nota || '—'}</span>
                  <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{new Date(v.publicada_at).toLocaleDateString(LOCALE)}{v.por ? ` · ${v.por}` : ''}</span>
                  {!v.es_actual && <Button size="sm" variant="outline" onClick={() => void revertir(v.id, v.version)}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Revertir</Button>}
                </div>
              ))}
              {!(ver?.versiones ?? []).length && <div className="py-6 text-center text-muted-foreground">Aún no hay versiones.</div>}
            </div>
          </PanelIntegrado>
        )}
        {picker && (
          <MediaPicker tipo={picker.tipo} onClose={() => setPicker(null)}
            onPick={(a: MediaAsset) => { if (picker.tipo === 'imagen') setEsc(picker.i, { url: a.datos, foco_x: a.foco_x, foco_y: a.foco_y }); else setEsc(picker.i, { url: a.url }); }} />
        )}
      </div>
    );
  }

  // ----- Lista -----
  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><LayoutList className="w-5 h-5" /> Plantillas</h1>
        <Button variant="outline" className="ml-auto" onClick={() => setRubroOpen(true)}><Sparkles className="w-4 h-4 mr-1" /> Desde rubro</Button>
        <Button onClick={() => setEdit({ nombre: '', escenas: [{ tipo: 'catalogo', dur: 9 }, { tipo: 'carta', dur: 16, titulo: 'La carta' }], es_activo: true })}>
          <Plus className="w-4 h-4 mr-1" /> Nueva plantilla
        </Button>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Plantilla</th>
            <th className="py-2 px-3 text-left">Escenas</th><th className="py-2 px-3 text-left">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {plantillas.map((p, i) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{p.nombre} {p.codigo && <span className="text-xs text-muted-foreground">{p.codigo}</span>}</td>
                <td className="py-2 px-3 text-muted-foreground">{(p.escenas ?? []).map((e) => e.tipo).join(' → ') || '—'}</td>
                <td className="py-2 px-3">{p.es_activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(p)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('plantilla', p.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!plantillas.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin plantillas.</td></tr>}
          </tbody>
        </table>
      </div>

      {rubroOpen && (
        <PanelIntegrado title="Empezar desde un rubro" onClose={() => setRubroOpen(false)} wide>
          <div className="grid sm:grid-cols-2 gap-3">
            {RUBROS.map((r) => (
              <button key={r.key} className="border rounded-lg p-4 text-left hover:ring-2 ring-primary transition" onClick={() => void crearDesdeRubro(r)}>
                <div className="flex items-center gap-2 font-medium"><r.Icon className="w-5 h-5 text-primary" /> {r.label}</div>
                <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
                <div className="text-[11px] text-muted-foreground mt-2">{r.escenas.map((e) => e.tipo).join(' → ')} · tema {r.tema}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground pt-2">Se crea una plantilla lista con estas escenas y el tema recomendado; luego la ajustas y la <strong>publicas</strong>.</p>
        </PanelIntegrado>
      )}
    </div>
  );
}
